import type { CheckoutQuote, OfferedMethodId, PaymentAttempt } from '../api/endpoints/planPayment';
import { formatMonthLabel } from '../admin/lib/month';
import { formatWholeDollars } from '../components/PlanPicker';
import {
  annualPriceCents,
  effectiveCycle,
  planLabel,
  PLAN_LIST_PRICE_CENTS,
  type BillingCycle,
  type PlanTier,
} from './plan';

/**
 * The words on /pago, kept pure so they are tested apart from the screen:
 * the order summary, how each way to pay is named, and what the owner is told
 * about an attempt that did not become a payment.
 */

export type OrderSummary = {
  title: string;
  cycle: string;
  total: string;
  note: string;
  rows: { label: string; value: string }[];
};

type MethodCopy = { title: string; sub: string; verb: string };

export const METHOD_COPY: Readonly<Record<OfferedMethodId, MethodCopy>> = {
  stripe: {
    title: 'Tarjeta de crédito o débito',
    sub: 'Pago automático en la página segura de Stripe.',
    verb: 'con tarjeta',
  },
  tarjeta: {
    title: 'Tarjeta de crédito o débito',
    sub: 'Pagas en la página segura de PagueloFácil.',
    verb: 'con tarjeta',
  },
  paypal: {
    title: 'PayPal',
    sub: 'Con tu cuenta PayPal, en su página.',
    verb: 'con PayPal',
  },
  yappy: {
    title: 'Yappy',
    sub: 'Pagas desde tu app y nos pasas el número de operación.',
    verb: 'por Yappy',
  },
  transferencia: {
    title: 'Transferencia bancaria',
    sub: 'Transfieres a la cuenta de Dale y nos pasas la referencia.',
    verb: 'por transferencia',
  },
};

const FAILURE_TEXT: Record<string, string> = {
  provider_unavailable: 'No pudimos abrir el pago. Intenta de nuevo o elige otro método.',
};
const DECLINED_TEXT = 'Tu pago no se completó. Intenta de nuevo o elige otro método.';

export function orderSummary(quote: CheckoutQuote): OrderSummary {
  const title = `Plan ${planLabel(quote.plan)}`;
  const monthly = formatWholeDollars(quote.monthlyPriceCents);
  if (quote.billingCycle === 'monthly') {
    return {
      title,
      cycle: 'Pago mensual',
      total: formatWholeDollars(quote.amountCents),
      note: `${monthly} al mes, se renueva cada mes`,
      rows: [{ label: `1 mes de Dale ${planLabel(quote.plan)}`, value: formatWholeDollars(quote.amountCents) }],
    };
  }
  const saving = formatWholeDollars(quote.savingsCents);
  return {
    title,
    cycle: 'Pago anual',
    total: formatWholeDollars(quote.amountCents),
    note: `Ahorras ${saving} frente a pagar mes a mes`,
    rows: [
      { label: `${quote.monthsCovered} meses a ${monthly}`, value: formatWholeDollars(quote.amountCents + quote.savingsCents) },
      { label: 'Descuento por pago anual', value: `−${saving}` },
    ],
  };
}

export function paidUntilLine(lastCoveredMonth: string): string {
  return `Pagado hasta ${formatMonthLabel(lastCoveredMonth)}`;
}

export function payButtonLabel(method: 'stripe' | 'tarjeta' | 'paypal', amountCents: number): string {
  return `Pagar ${formatWholeDollars(amountCents)} ${METHOD_COPY[method].verb}`;
}

/** The only pages /pago ever sends an owner to; the server checks the same hosts. */
const CHECKOUT_ORIGINS: readonly string[] = [
  'https://checkout.stripe.com',
  'https://checkout.paguelofacil.com',
  'https://www.paypal.com',
  'https://www.sandbox.paypal.com',
];

export function isTrustedCheckoutUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && CHECKOUT_ORIGINS.includes(url.origin);
  } catch {
    return false;
  }
}

/** What to tell the owner about an attempt that did not become money; undefined when it has its own screen. */
export function attemptNotice(attempt: PaymentAttempt): { tone: 'danger'; text: string } | undefined {
  if (attempt.status === 'fallido') {
    return { tone: 'danger', text: FAILURE_TEXT[attempt.statusNote ?? ''] ?? DECLINED_TEXT };
  }
  if (attempt.status === 'rechazado') {
    const reference = attempt.reference ? ` con referencia ${attempt.reference}` : '';
    return {
      tone: 'danger',
      text: `No pudimos confirmar tu pago${reference}. Revisa el número o intenta con otro método.`,
    };
  }
  return undefined;
}

export type CardStepCopy = { lede: string; action: string };

/**
 * The words of the card step for a paid plan: what Stripe charges today and
 * how often, from the monthly price on file (the list price when none is
 * given). Published prices exclude tax, which Stripe adds apart for the
 * buyer's address. On the free trial (`freeUntil`, the trial end already
 * written as a date, and only while Stripe can still hold the charge),
 * nothing is charged today: the first charge is that day. Independientes
 * pays nothing up front, so it has no card step (`CardStep`).
 */
export function cardStepCopy(input: { plan: PlanTier; cycle: BillingCycle; monthlyPriceCents?: number; freeUntil?: string }): CardStepCopy {
  const monthly = input.monthlyPriceCents ?? PLAN_LIST_PRICE_CENTS[input.plan];
  const annual = effectiveCycle(input.plan, input.cycle) === 'annual';
  const amount = formatWholeDollars(annual ? annualPriceCents(monthly) : monthly);
  const period = annual ? { price: 'al año', renews: 'cada año' } : { price: 'al mes', renews: 'cada mes' };
  // The price is what the business pays: no tax line next to it (Martín, 2026-10-28).
  const plan = `Plan ${planLabel(input.plan)}, ${amount} ${period.price}.`;
  if (input.freeUntil) {
    return {
      lede: `${plan} Hoy no se cobra nada: el primer cobro es el ${input.freeUntil} y luego se renueva ${period.renews} con la misma tarjeta.`,
      action: 'Guardar tarjeta',
    };
  }
  return {
    lede: `${plan} Se cobra hoy y se renueva ${period.renews} con la misma tarjeta.`,
    action: `Agregar tarjeta y pagar ${amount}`,
  };
}
